import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import { getRsaPlans } from '../../api/rsa.api';
import '../../styles/user-dashboard.css';

const PLAN_COLORS = { basic: '#0ea5e9', standard: '#2563eb', premium: '#7c3aed' };
const PLAN_GRADIENTS = {
  basic: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
  standard: 'linear-gradient(135deg,#2563eb,#1e40af)',
  premium: 'linear-gradient(135deg,#7c3aed,#6d28d9)',
};

const RsaJoin = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const preselected = location.state?.planSlug;

  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(preselected || 'standard');
  const [form, setForm] = useState({
    customerName: user?.name || '',
    customerPhone: user?.phone || '',
    customerEmail: user?.email || '',
    customerAddress: '',
    registrationNo: '',
    vehicleType: '',
    fuelType: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getRsaPlans()
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectedPlan = plans.find((p) => p.slug === selected) || plans[1];
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleNext = (e) => {
    e.preventDefault();
    if (!form.customerName || !form.customerPhone) {
      setError('Name and phone are required.');
      return;
    }
    setError('');
    if (!selectedPlan) { setError('Please select a plan.'); return; }
    navigate('/payment', {
      state: {
        type: 'rsa',
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        amount: selectedPlan.price,
        memberDetails: { ...form, planId: selectedPlan.id, planPrice: selectedPlan.price },
      },
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="auth-spinner" />
      </div>
    );
  }

  return (
    <div className="rsa-join-page">
      <div className="rsa-join-container">
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>
          Join RSA Membership
        </h1>
        <p style={{ fontSize: '0.9375rem', color: '#64748b', marginBottom: '2rem' }}>
          Select a plan and enjoy 24/7 roadside protection across Jaipur.
        </p>

        {/* Plan Selector */}
        <div className="rsa-join-plans">
          {plans.map((plan) => (
            <div
              key={plan.slug}
              className={`rsa-join-plan-card ${selected === plan.slug ? 'selected' : ''}`}
              onClick={() => setSelected(plan.slug)}
              style={selected === plan.slug ? { borderColor: PLAN_COLORS[plan.slug] || '#2563eb' } : {}}
            >
              {plan.isPopular && (
                <div style={{
                  display: 'inline-block', padding: '0.15rem 0.6rem', borderRadius: 20,
                  background: PLAN_GRADIENTS[plan.slug], color: '#fff', fontSize: '0.7rem',
                  fontWeight: 700, marginBottom: '0.5rem',
                }}>
                  POPULAR
                </div>
              )}
              <div className="rsa-join-plan-name">{plan.name}</div>
              <div className="rsa-join-plan-price" style={{ color: PLAN_COLORS[plan.slug] || '#2563eb' }}>
                ₹{Number(plan.price).toLocaleString('en-IN')}
                <span className="rsa-join-plan-period">/year</span>
              </div>
              <div className="rsa-join-plan-feature">
                {plan.servicesPerYear} services · {plan.responseTimeMinutes} min response
              </div>
            </div>
          ))}
        </div>

        {/* Member Details Form */}
        <div className="book-service-card" style={{ background: '#fff', borderRadius: 16, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0f172a', marginBottom: '1.5rem' }}>
            Your Details
          </h2>

          {error && (
            <div className="auth-error-box" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <form className="book-service-form" onSubmit={handleNext}>
            <div className="book-form-row">
              <div className="book-form-field">
                <label className="book-form-label">Full Name *</label>
                <input name="customerName" className="book-form-input" value={form.customerName} onChange={handleChange} required />
              </div>
              <div className="book-form-field">
                <label className="book-form-label">Phone *</label>
                <input name="customerPhone" className="book-form-input" value={form.customerPhone} onChange={handleChange} required />
              </div>
            </div>
            <div className="book-form-field">
              <label className="book-form-label">Email</label>
              <input type="email" name="customerEmail" className="book-form-input" value={form.customerEmail} onChange={handleChange} />
            </div>
            <div className="book-form-field">
              <label className="book-form-label">Address</label>
              <input name="customerAddress" className="book-form-input" placeholder="Flat 5, Mansarovar, Jaipur" value={form.customerAddress} onChange={handleChange} />
            </div>
            <div className="book-form-row">
              <div className="book-form-field">
                <label className="book-form-label">Vehicle Registration</label>
                <input name="registrationNo" className="book-form-input" placeholder="RJ14 XX 0000" value={form.registrationNo} onChange={handleChange} />
              </div>
              <div className="book-form-field">
                <label className="book-form-label">Vehicle Type</label>
                <select name="vehicleType" className="book-form-select" value={form.vehicleType} onChange={handleChange}>
                  <option value="">Select</option>
                  <option value="hatchback">Hatchback</option>
                  <option value="sedan">Sedan</option>
                  <option value="suv">SUV</option>
                  <option value="muv">MUV</option>
                  <option value="luxury">Luxury</option>
                </select>
              </div>
            </div>
            <div className="book-form-field">
              <label className="book-form-label">Fuel Type</label>
              <select name="fuelType" className="book-form-select" value={form.fuelType} onChange={handleChange}>
                <option value="">Select</option>
                <option value="petrol">Petrol</option>
                <option value="diesel">Diesel</option>
                <option value="cng">CNG</option>
                <option value="ev">Electric</option>
              </select>
            </div>

            {selectedPlan && (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', border: '1.5px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.8125rem', color: '#64748b', marginBottom: '0.25rem' }}>Selected Plan</div>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>
                  {selectedPlan.name} — <span style={{ color: PLAN_COLORS[selectedPlan.slug] }}>
                    ₹{Number(selectedPlan.price).toLocaleString('en-IN')}/year
                  </span>
                </div>
              </div>
            )}

            <button type="submit" className="book-submit-btn">
              Proceed to Payment
              <svg className="w-4 h-4 inline ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RsaJoin;
